import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ensureStages } from '@/features/projects/api'
import { toTask } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { DueSoonBucket, Page, TaskListItem } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { ensureUserIndex } from '@/shared/hooks/use-users'
import { toSearchParams } from '@/shared/lib/query-params'
import type { Bucket } from './buckets'
import { PARAM } from './use-due-soon-filters'

/**
 * Los filtros de la URL están en castellano y son de pantalla; los del backend
 * son otros. `estado=vencidas` no viaja: el servidor ya separa lo vencido en su
 * propio bucket, así que recortar a "solo vencidas" es quedarse con ese bucket.
 */
export function toDueSoonQuery(filters: Record<string, string>): string {
  return toSearchParams({
    horizon_days: filters[PARAM.horizon],
    assignee_id: filters[PARAM.assignee],
    company_id: filters[PARAM.company],
  })
}

/**
 * Lo que vence.
 *
 * La ruta es `tasks/due-soon` (no `due-soon`) y **devuelve los buckets ya
 * armados por el servidor**: vencidas, hoy, esta semana y próximas dos semanas,
 * con el mismo reparto que usan las notificaciones de Slack y del email.
 *
 * Lo que se devuelve acá es **exactamente eso**: la clave, el orden y el
 * reparto son los del servidor. El nombre de cada tramo lo escribe la pantalla
 * en el idioma de quien mira (`BUCKET_LABEL_KEY`), porque el servidor manda la
 * misma respuesta a los tres idiomas; su `label` queda de respaldo para una
 * clave que la pantalla todavía no conozca. Antes se pedían los buckets y se
 * tiraba la asignación para recalcularla en el cliente con otra regla, y las
 * dos reglas discrepaban: el servidor corta "Esta semana" en el domingo de la
 * semana en curso, el cliente cortaba a 7 días rodantes. Un viernes, la misma
 * tarea salía en dos tramos distintos en el mail y en la pantalla.
 *
 * Lo único que se transforma son las tareas, de `TaskListItem` al modelo de
 * pantalla.
 */
export function useDueSoonBuckets(filters: Record<string, string>) {
  const client = useQueryClient()
  // El estado no viaja al servidor: el servidor ya separa lo vencido en su
  // propio bucket, así que filtrar es vaciar los tramos que sobran. Se vacían y
  // no se esconden: el orden de los cuatro tramos es fijo justamente para que
  // no haya que buscar dónde quedó cada cosa.
  const estado = filters[PARAM.state]
  const vaciar = (key: string): boolean =>
    (estado === 'vencidas' && key !== 'overdue') || (estado === 'por-vencer' && key === 'overdue')

  return useQuery({
    queryKey: qk.dueSoon.list(filters),
    queryFn: async (): Promise<Bucket[]> => {
      const [users, stages, buckets] = await Promise.all([
        ensureUserIndex(client),
        ensureStages(client, 'task'),
        api.get<DueSoonBucket[]>(`tasks/due-soon${toDueSoonQuery(filters)}`),
      ])

      return buckets.map((bucket) => ({
        key: bucket.key,
        label: bucket.label,
        tasks: vaciar(bucket.key)
          ? []
          : bucket.tasks.map((item) => toTask(item, { users, taskStages: stages })),
      }))
    },
  })
}

/** `/me/tasks`: todo lo mío, de todas las empresas. */
export function useMyTasks() {
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.tasks.mine(),
    queryFn: async () => {
      const [users, stages, page] = await Promise.all([
        ensureUserIndex(client),
        ensureStages(client, 'task'),
        api.get<Page<TaskListItem>>('tasks/mine?limit=200'),
      ])
      return page.items.map((item) => toTask(item, { users, taskStages: stages }))
    },
  })
}
